import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';

import {
  useGetWatchersQuery,
  useJoinWatchMutation,
  useLeaveWatchMutation,
} from '@/services/socialApi';
import { cn } from '@/lib/cn';
import { inCodaPerTorneo } from '@/lib/qsocials/codaWatch';

import { UserAvatar } from './UserAvatar';

interface CoWatchOverlayProps {
  tournamentId: string;
  className?: string;
}

const MAX_AVATARS = 6;
const POLL_MS = 10_000;

/**
 * "Friends watching this tournament with you" strip. Mounted at the top of
 * the tournament live page. Joins the watcher set while the view is mounted AND
 * visible, leaves on unmount and when the page goes to the background (a new
 * join when it is visible again), and polls `/social/watch/:tournamentId`
 * every 10s. One leave per join, never a leave without a join before it: the
 * server renews the 24 h TTL of the whole watcher set on every join, so a
 * join that is never undone counts everyone who ever opened the page.
 * Join and leave of the same tournament are queued (`codaWatch.ts`): each one
 * starts after the response of the previous one, so a late `srem` cannot land
 * after a newer `sadd`.
 *
 * Future (Wave 1+): replace the polling with a Centrifugo subscription on
 * `tournament:<id>:watchers` once the qsocials broadcast bridge is live —
 * see `useCentrifugoChannel` for the wiring. The HTTP polling is the
 * fallback that always works.
 */
export function CoWatchOverlay({ tournamentId, className }: CoWatchOverlayProps) {
  const { t } = useTranslation('general');
  const [joinWatch] = useJoinWatchMutation();
  const [leaveWatch] = useLeaveWatchMutation();
  const watchers = useGetWatchersQuery(tournamentId, {
    pollingInterval: POLL_MS,
    refetchOnFocus: true,
  });

  useEffect(() => {
    if (!tournamentId) return;
    // `joined` tiene il conto: un solo leave per join, nessun leave senza join
    // prima, nessuna raffica su hidden/visible ripetuti.
    let joined = false;
    const join = () => {
      if (joined) return;
      joined = true;
      inCodaPerTorneo(tournamentId, () => joinWatch({ tournamentId }).unwrap()).catch((err) => {
        if (import.meta.env.DEV) console.debug('[cowatch] joinWatch failed', err);
      });
    };
    const leave = () => {
      if (!joined) return;
      joined = false;
      inCodaPerTorneo(tournamentId, () => leaveWatch({ tournamentId }).unwrap()).catch((err) => {
        if (import.meta.env.DEV) console.debug('[cowatch] leaveWatch failed', err);
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') leave();
      else join();
    };
    // Montato in background: il join aspetta il ritorno visibile.
    if (document.visibilityState !== 'hidden') join();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      leave();
    };
  }, [tournamentId, joinWatch, leaveWatch]);

  const data = watchers.data?.watch;
  const total = data?.watcherCount ?? 0;
  const list = data?.watchers ?? [];
  const visible = list.slice(0, MAX_AVATARS);
  const overflow = Math.max(0, total - visible.length);

  if (watchers.isLoading || total === 0) {
    // Render a minimal pill anyway so the UI shows the affordance — empty
    // state reads "Be the first to watch" which doubles as a soft CTA.
    return (
      <div
        className={cn(
          'mx-4 flex items-center gap-2 rounded-xl border border-border bg-bg-secondary px-3 py-2',
          className,
        )}
      >
        <Eye aria-hidden className="h-4 w-4 text-fg-muted" />
        <span className="text-xs text-fg-muted">
          {watchers.isLoading
            ? t('social.cowatch_loading', { defaultValue: 'Loading watchers…' })
            : t('social.cowatch_empty', {
                defaultValue: 'Be the first to watch live.',
              })}
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'mx-4 flex items-center justify-between gap-3 rounded-xl border border-border bg-bg-secondary px-3 py-2',
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <Eye aria-hidden className="h-4 w-4 text-brand" />
        <span className="text-xs font-medium text-fg">
          {t('social.cowatch_count', {
            defaultValue: '{{count}} watching',
            count: total,
          })}
        </span>
      </div>
      <div className="flex items-center">
        {visible.map((userId, idx) => (
          <span
            key={userId}
            className="-ml-2 rounded-full ring-2 ring-bg-secondary first:ml-0"
            style={{ zIndex: visible.length - idx }}
          >
            <UserAvatar userId={userId} size="sm" />
          </span>
        ))}
        {overflow > 0 && (
          <span
            className={cn(
              '-ml-2 inline-flex h-8 w-8 items-center justify-center rounded-full',
              'bg-bg-tertiary text-[10px] font-semibold text-fg-muted ring-2 ring-bg-secondary',
            )}
          >
            +{overflow}
          </span>
        )}
      </div>
    </div>
  );
}
